import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ApiService } from '../../../api/services/api';

import { ApiRequestBuilder } from '../../../api/builder/api-request';
import {
  deleteAllCookies,
  deleteCookie,
  getCookie,
  setCookie,
} from '../../../utils/browser';
import { find } from 'lodash';
import { StorageDbService } from '../../../storage-db';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  TenantSummary,
  User,
  UserItemStorageDb,
  UserScope,
  UserScopeRole,
} from '../../../../../../backend-core-lib/app/common-shared/account/models';
import { MsalService } from '@azure/msal-angular';

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  isGuest = false;
  user?: User;
  environmentsSynced = false;
  isLoggingMsal = false;
  onSessionChanged: EventEmitter<User | undefined> = new EventEmitter();
  onReloadTenant: EventEmitter<boolean> = new EventEmitter();

  private readonly forceToLoginSubject = new BehaviorSubject<boolean>(false);
  readonly forceToLogin$ = this.forceToLoginSubject.asObservable();

  private destroy$ = new Subject<void>();
  constructor(
    private apiService: ApiService,
    private storageDbService: StorageDbService,
    private translateService: TranslateService,
    private authService: MsalService,
  ) {
    this.apiService.onInvalidAccessRights
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('Heree');
        this.changeUserOffline(undefined, true);
      });
  }
  setFailedRecoverMsal(): void {
    if (!this.user && !this.apiService.getSessionToken()) {
      this.forceToLoginSubject.next(true);
    }
  }
  getForceToLogin(): boolean {
    return this.forceToLoginSubject.getValue();
  }
  setForceToLogin(value: boolean) {
    this.forceToLoginSubject.next(value);
  }
  changeUserOffline(user?: User, emitEvent = true): void {
    const previousUser = this.user;
    this.user = user;
    if (emitEvent && previousUser !== this.user) {
      this.onSessionChanged.emit(user);
    }
  }
  areEnvironmentsSynced(): boolean {
    return this.environmentsSynced;
  }
  setEnvironmentsSynced(synced: boolean): void {
    this.environmentsSynced = synced;
  }

  getCurrentUserTenant(): string | undefined {
    return this.apiService.getTenant();
  }

  async getLinkedTenants(): Promise<TenantSummary[]> {
    const sessionRequest = new ApiRequestBuilder('sessionTenants').build();
    const resultApi = await this.apiService.callApi(sessionRequest);
    if (!resultApi || resultApi.status === 'error') {
      return [];
    }
    return resultApi.data;
  }

  getTenantRoles(tenant: string): UserScopeRole[] {
    const toReturn: UserScopeRole[] = [];
    if (tenant && this.user?.scopes?.length) {
      this.user.scopes.forEach((scope) => {
        if (scope.tenant === tenant && toReturn.indexOf(scope.role) < 0) {
          toReturn.push(scope.role);
        }
      });
    }
    return toReturn;
  }
  isAdminInCurrentTenant(): boolean {
    const currentTenant = this.getCurrentUserTenant();
    if (!currentTenant) {
      return false;
    }
    return this.getTenantRoles(currentTenant).indexOf('ADMIN') >= 0;
  }

  async signUpUser(
    email: string,
    password: string,
    recaptcha: string,
  ): Promise<string> {
    const signUpRequest = new ApiRequestBuilder('signUp')
      .setRecaptchaCode(recaptcha)
      .setBody({ email: email, password: password })
      .build();

    const resultApi = await this.apiService.callApi(signUpRequest);
    if (!resultApi || resultApi.status === 'error') {
      return resultApi.data || 'ServerUnreachable';
    }
    return this.loginUser(email, password, false);
  }

  async existSessionAsGuest(): Promise<boolean> {
    return !!(await this.getOfflineSession(true, false));
  }

  async getOfflineSessionWithEmail(
    email: string,
  ): Promise<UserItemStorageDb | undefined> {
    const currentUsersItems: UserItemStorageDb[] =
      await this.storageDbService.getAllTableItems<UserItemStorageDb>(
        'offlineUsers',
      );
    let foundItem = find(currentUsersItems, (item) => {
      return !item.guest && item.email == email;
    });
    return foundItem;
  }
  async getOfflineSessionNonGuest(): Promise<UserItemStorageDb | undefined> {
    const currentUsersItems: UserItemStorageDb[] =
      await this.storageDbService.getAllTableItems<UserItemStorageDb>(
        'offlineUsers',
      );
    let foundItem = find(currentUsersItems, (item) => {
      return !item.guest;
    });
    return foundItem;
  }
  async getOfflineSessionGuest(): Promise<UserItemStorageDb | undefined> {
    const currentUsersItems: UserItemStorageDb[] =
      await this.storageDbService.getAllTableItems<UserItemStorageDb>(
        'offlineUsers',
      );
    let foundItem = find(currentUsersItems, (item) => {
      return item.guest;
    });
    return foundItem;
  }

  async getOfflineSession(
    isGuest = true,
    createIfNotPresent = false,
  ): Promise<UserItemStorageDb | undefined> {
    const currentUsersItems: UserItemStorageDb[] =
      await this.storageDbService.getAllTableItems<UserItemStorageDb>(
        'offlineUsers',
      );
    let foundItem = find(currentUsersItems, (item) => {
      if (isGuest) {
        return item.guest;
      } else {
        return !item.guest && item.email == this.getUser()?.email;
      }
    });
    if (foundItem) {
      return foundItem;
    }
    if (!foundItem && createIfNotPresent) {
      foundItem = await this.storageDbService.addTableItem<UserItemStorageDb>(
        {
          email: this.getUser()?.email as string,
          guest: isGuest,
          data: this.getUser() as User,
        },
        'offlineUsers',
      );
    }
    return foundItem;
  }

  async changeUserLanguage(language: string): Promise<void> {
    if (this.user) {
      this.user.language = language;
      if (this.isGuest) {
        await this.saveOfflineUser();
      } else {
        const languageRequest = new ApiRequestBuilder('changeLanguage')
          .setBody({ language: language })
          .build();
        await this.apiService.callApiGetData(languageRequest, 'changeLanguage');
      }
      this.changeUserOffline(this.user);
    }
  }

  isLoggingAlreadyUserMsal(): boolean {
    return this.isLoggingMsal;
  }
  async loginUserMsal(
    idToken: string | undefined,
    expiration: number | undefined,
  ): Promise<string> {
    if (
      !idToken ||
      !expiration ||
      !((expiration || 0) > new Date().valueOf() / 1000)
    ) {
      return 'ErrorTokenExpired';
    }

    this.isLoggingMsal = true;
    this.isGuest = false;
    try {
      const loginRequest = new ApiRequestBuilder('loginMsal')
        .showLoading()
        .setBody({ token: idToken })
        .build();

      const resultApi = await this.apiService.callApi(loginRequest);
      if (!resultApi || resultApi.status === 'error') {
        return resultApi.data || 'ServerUnreachable';
      }
      console.log(resultApi);
      console.log(resultApi.data.accessToken);
      this.setForceToLogin(false);
      await this.loginUserWithUserData(resultApi.data, true, true, true);
      this.isLoggingMsal = false;
      return '';
    } catch (e) {
      this.isLoggingMsal = false;
      return 'ServerUnreachable';
    }
  }

  async loginUser(
    email: string,
    password: string,
    keepLoggedIn: boolean,
    recaptchaCode = '',
    code2FA = '',
  ): Promise<string> {
    this.isGuest = false;
    const loginRequest = new ApiRequestBuilder('login')
      .setAuth2FACode(code2FA)
      .setRecaptchaCode(recaptchaCode)
      .setBody({ email: email, password: password })
      .build();

    const resultApi = await this.apiService.callApi(loginRequest);
    if (!resultApi || resultApi.status === 'error') {
      return resultApi.data || 'ServerUnreachable';
    }
    console.log(resultApi);

    await this.loginUserWithUserData(resultApi.data, keepLoggedIn, true, true);
    return '';
  }

  async loginUserWithUserData(
    user: User,
    keepLoggedIn: boolean,
    emitSessionChange = true,
    mergeLocalSession = false,
    overwriteAccessToken = '',
  ): Promise<void> {
    this.isGuest = false;
    const accessToken = overwriteAccessToken || user.accessToken;
    let toUseTenant = user.scopes?.length ? user.scopes[0].tenant : '';
    const tenantCookie = getCookie('tenant');
    if (
      tenantCookie &&
      user.scopes?.length &&
      find(user.scopes, { tenant: tenantCookie })
    ) {
      toUseTenant = tenantCookie;
    }

    this.apiService.setSessionToken(accessToken);
    await this.apiService.setTenant(toUseTenant);
    if (keepLoggedIn) {
      setCookie('accessToken', accessToken, 1);
    } else {
      deleteCookie('accessToken');
    }
    await this.onLoginResponse(user, emitSessionChange, mergeLocalSession);
  }

  async logoutUser(doApiRequest = true): Promise<boolean> {
    console.log('logoutUser');
    await this.storageDbService.clearTableItems<UserItemStorageDb>(
      'offlineUsers',
    );
    if (!this.isGuest && doApiRequest) {
      const logoutRequest = new ApiRequestBuilder('logout').build();
      await this.apiService.callApi(logoutRequest);
    }

    deleteCookie('accessToken');
    this.apiService.setSessionToken(undefined);
    deleteCookie('tenant');
    await this.apiService.setTenant(undefined);
    this.user = undefined;
    this.isGuest = false;

    // await this.authService.logout().toPromise();
    this.onSessionChanged.emit(undefined);

    const activeAccount = this.authService.instance.getActiveAccount();
    console.log(activeAccount);
    deleteAllCookies();
    localStorage.clear();
    if (activeAccount) {
      await this.authService.instance.clearCache({ account: activeAccount });
      await this.authService.instance.logoutRedirect();
    }

    return true;
  }

  private async onLoginResponse(
    user: User,
    emitSessionChange = true,
    mergeLocalSession = false,
  ): Promise<void> {
    user.accessToken = '';
    let mergedUserSession = user;
    if (mergeLocalSession) {
      const localUserItem = await this.getOfflineSessionWithEmail(user.email);
      mergedUserSession = await this.mergeUserSessionWithLocal(
        localUserItem?.data,
        user,
      );
    }

    this.user = mergedUserSession;
    await this.storageDbService.clearTableItems<UserItemStorageDb>(
      'offlineUsers',
    );
    await this.storageDbService.addTableItem<UserItemStorageDb>(
      {
        email: this.user?.email || '',
        guest: !this.user?.email,
        data: this.user,
      },
      'offlineUsers',
    );

    if (emitSessionChange) {
      this.emitSessionChanged();
    }
  }

  private async mergeUserSessionWithLocal(
    localUser: User | undefined,
    onlineUser: User,
  ) {
    if (!localUser || localUser.email !== onlineUser.email) {
      return onlineUser;
    }
    let anyChangeFound = false;
    if (anyChangeFound) {
      // Check the avatar
      // Check the language
      /*if (onlineUser.language !== localUser.language) {
        const languageRequest = new ApiRequestBuilder('changeLanguage')
          .setBody({ language: localUser.language })
          .build();
        await this.apiService.callApiGetData(languageRequest);
      }
      if (onlineUser.avatarId !== localUser.avatarId) {
        const avatarRequest = new ApiRequestBuilder('changeAvatar')
          .setBody({ avatarId: localUser.avatarId })
          .build();
        await this.apiService.callApiGetData(avatarRequest);
      }*/
    }

    return onlineUser;
  }

  public emitSessionChanged() {
    this.onSessionChanged.emit(this.user);
  }

  public emitReloadTenant(): void {
    this.onReloadTenant.emit(true);
  }

  async saveOfflineUser(): Promise<void> {
    const offlineUserInDB = this.isGuest
      ? await this.getOfflineSession(true, false)
      : await this.getOfflineSession(false, true);
    if (offlineUserInDB) {
      await this.storageDbService.putTableItem<UserItemStorageDb>(
        offlineUserInDB.id as number,
        {
          id: offlineUserInDB.id,
          email: offlineUserInDB.email,
          guest: offlineUserInDB.guest,
          data: this.getUser() as User,
        },
        'offlineUsers',
      );
    }
  }

  async refreshSessionInfo(mergeLocalSession = false): Promise<string> {
    // Returns the error code received
    if (!this.apiService.getSessionToken()) {
      return 'NoSessionToken';
    }
    const sessionRequest = new ApiRequestBuilder('session').build();

    const resultApi = await this.apiService.callApi(sessionRequest);
    if (!resultApi || resultApi.status === 'error') {
      return resultApi.data || 'ServerUnreachable';
    }

    const user = resultApi.data as User;
    await this.onLoginResponse(user, true, mergeLocalSession);
    return '';
  }

  isSessionActive(): boolean {
    return !!this.user;
  }
  getUser(): User | undefined {
    return this.user;
  }
  async tryRecoverSession(): Promise<boolean> {
    // 0 - If already session active, do nothing
    if (this.isSessionActive()) {
      return true;
    }

    // 1 - If there is a non-guest session saved in indexDB
    //   1-1 - If there is a accessToken try to get the session
    //         -> If is success: All cool set the session through mergeSessions
    //         -> If it fails:
    //              -ServerUnreachable -> Set the session as offline
    //              -Error Authenticating -> Delete the accessToken from the cookie and redirect
    //                                       to login screen presetting the email field
    //   1-2 - If there is no accessToken-> redirect to login screen presetting the email field (return false)
    // 2 - Else If there is a guest session saved in indexDB
    //   2-1 - Load it
    // 3 - Else (No session in indexDB) redirect to login (return false)
    // X . After every login with the server, call mergeSession
    const accessTokenCookie = getCookie('accessToken');
    const tenantCookie = getCookie('tenant');
    const nonGuestSession = await this.getOfflineSessionNonGuest();
    console.log('nonGuestSession');
    console.log(nonGuestSession);
    // const guestSession = await this.getOfflineSessionGuest();
    if (nonGuestSession) {
      if (accessTokenCookie) {
        this.apiService.setSessionToken(accessTokenCookie);
        await this.apiService.setTenant(tenantCookie || '');
        const errorCode = await this.refreshSessionInfo(true);
        if (errorCode) {
          if (errorCode !== 'ServerUnreachable') {
            deleteCookie('accessToken');
            return false;
          } else {
            await this.loginUserWithUserData(
              nonGuestSession.data,
              true,
              true,
              true,
              accessTokenCookie,
            );
          }
        } else {
          return true;
        }
      } else {
        return false;
      }
    } /*else if (guestSession) {
      await this.loginAsGuest();
    } */ else {
      return false;
    }

    return true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
